import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { addDays, format, isSameDay, startOfWeek } from 'date-fns';
import { Calendar, ChevronLeft, ChevronRight, RefreshCw } from 'lucide-react';
import { Link } from 'react-router-dom';
import { eventsOnDay, localDateString } from '@cerebro-diem/core';
import { errorMessage } from '../../services/errors';
import { anyCalendarConnected, calendarConnections, calendarDays, calendarKeys, syncCalendars } from '../../services/calendar';
import { useProfile } from '../../services/profile';
import { iconButton, primaryButton } from '../../components/styles';
import CalendarEventItem from './CalendarEventItem';

/**
 * A week of the user's calendars, read-only: every calendar not off, from each connected source,
 * busy events marked. Connecting and choosing calendars is on Integrations.
 */
export default function CalendarPage() {
  const [weekStart, setWeekStart] = useState(() => startOfWeek(new Date()));
  const queryClient = useQueryClient();
  const from = localDateString(weekStart);
  const to = localDateString(addDays(weekStart, 7));
  const timeZone = useProfile().data?.timezone ?? undefined;

  const connection = useQuery({ queryKey: calendarKeys.connections, queryFn: calendarConnections });
  const connected = connection.isSuccess && anyCalendarConnected(connection.data);
  const events = useQuery({ queryKey: calendarKeys.days(from, to), queryFn: () => calendarDays(from, to), enabled: connected });
  const sync = useMutation({
    mutationFn: () => syncCalendars(connection.data!),
    onSettled: () => queryClient.invalidateQueries({ queryKey: calendarKeys.all }),
  });

  const weekDays = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-4">
          <Calendar className="w-8 h-8 text-primary" />
          <h1 className="text-2xl font-bold">Calendar</h1>
        </div>
        {connected && (
          <button type="button" onClick={() => sync.mutate()} disabled={sync.isPending} className={`${primaryButton} inline-flex items-center gap-2`}>
            <RefreshCw className={`w-4 h-4 ${sync.isPending ? 'animate-spin' : ''}`} />
            Sync
          </button>
        )}
      </div>

      {connection.isError && <p className="text-sm text-error">Could not load your calendar connections: {errorMessage(connection.error)}</p>}
      {connection.isSuccess && !connected && (
        <p className="text-on-surface-variant">
          Connect a calendar on{' '}
          <Link to="/integrations" className="text-primary underline">
            Integrations
          </Link>{' '}
          to see your calendars here.
        </p>
      )}
      {sync.isError && <p role="alert" className="text-sm text-error">Sync failed: {errorMessage(sync.error)}</p>}
      {events.isError && <p className="text-sm text-error">Could not load your calendar: {errorMessage(events.error)}</p>}

      {connected && (
        <>
          <div className="flex items-center justify-between gap-2 bg-surface rounded-xl p-2 sm:p-4 shadow-sm">
            <button
              type="button"
              onClick={() => setWeekStart(addDays(weekStart, -7))}
              aria-label="Previous week"
              className={`${iconButton} shrink-0 p-2 hover:bg-surface-variant rounded-lg transition-colors`}
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <h2 className="min-w-0 text-center text-base sm:text-lg font-semibold">
              {format(weekStart, 'MMMM d')} - {format(addDays(weekStart, 6), 'MMMM d, yyyy')}
            </h2>
            <button
              type="button"
              onClick={() => setWeekStart(addDays(weekStart, 7))}
              aria-label="Next week"
              className={`${iconButton} shrink-0 p-2 hover:bg-surface-variant rounded-lg transition-colors`}
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>

          {/* A list of days on a phone (seven columns would be 33px each), a seven-day grid from md. */}
          <div className="grid grid-cols-1 gap-3 md:grid-cols-7 md:gap-4">
            {weekDays.map((day) => {
              const date = localDateString(day);
              const onDay = eventsOnDay(events.data ?? [], date, timeZone);
              return (
                <div key={date} className="md:min-h-[200px]">
                  <div
                    className={`flex items-baseline gap-2 px-3 py-2 md:block md:text-center md:p-2 rounded-t-xl ${
                      isSameDay(day, new Date()) ? 'bg-primary text-on-primary' : 'bg-surface-variant'
                    }`}
                  >
                    <div className="text-xs font-medium uppercase">{format(day, 'EEE')}</div>
                    <div className="text-lg font-bold">{format(day, 'd')}</div>
                  </div>
                  <div className="bg-surface rounded-b-xl p-2 space-y-2 md:min-h-[160px]">
                    {events.isPending ? (
                      <div className="animate-pulse space-y-2">
                        <div className="h-8 bg-surface-variant rounded" />
                      </div>
                    ) : (
                      onDay.map((event) => <CalendarEventItem key={event.id} event={event} day={date} timeZone={timeZone} />)
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
